/**
 * Formations LMS Video Player v2
 * A comprehensive video learning management system with AJAX navigation
 *
 * Features:
 * - Video controls (play/pause, mute, fullscreen, progress)
 * - AJAX navigation between lessons
 * - Video state preservation during navigation
 * - Mobile-optimized touch controls
 * - Loading overlay management
 * - Cross-browser compatibility
 */

class FormationsVideoPlayer {
  constructor() {
    this.elements = {};
    this.state = {
      currentVideo: {
        isMuted: false,
        volume: 1.0,
        currentTime: 0,
        isPlaying: false,
        playbackRate: 1.0
      },
      hasUserInteracted: false,
      isDragging: false,
      controlsTimeout: null,
      isControlAction: false
    };

    this.config = {
      isMobile: this.detectMobile(),
      sectionId: this.getSectionId(),
      videoCheckInterval: 500,
      videoCheckTimeout: 10000,
      loadingMinTime: 800,
      controlsHideDelay: 4000,
      maxNavigationAttempts: 20,
      navigationRetryDelay: 100
    };

    this.init();
  }

  /**
   * Initialize the video player
   */
  init() {
    this.bindElements();
    this.setupEventListeners();
    this.initializeVideoControls();
    this.initializeNavigationInterceptors();
    this.setupMobileControls();
    this.ensureVideoWrapper();
    this.setupVideoObserver();
    this.handleAutoPlay();
    this.initializeExamState();

    console.log("Formations Video Player initialized", {
      isMobile: this.config.isMobile,
      sectionId: this.config.sectionId
    });
  }

  /**
   * Initialize exam state - ensure clean state on page load
   */
  initializeExamState() {
    // Check if we're in exam mode based on exam parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasExamParam = urlParams.get('exam') === '1';
    
    // We're in exam mode only if we have exam=1 parameter
    const isExamMode = hasExamParam;
    
    if (isExamMode) {
      //console.log('📝 Page load: In exam mode - adding exam-loaded class and setting active state');
      document.body.classList.add('exam-loaded');
      document.body.classList.remove('lesson-loaded');
      
      // Set exam link as active and remove active from lessons
      document.querySelectorAll('.formations-lesson-item').forEach(item => {
        item.classList.remove('active');
      });
      // Add active to ALL exam links (mobile and desktop)
      document.querySelectorAll('.formations-exam-link').forEach(examLink => {
        examLink.classList.add('active');
      });
    } else {
     // console.log('📚 Page load: Not in exam mode - removing exam-loaded class');
      document.body.classList.remove('exam-loaded');
      
      // Make sure exam links are not active
      document.querySelectorAll('.formations-exam-link').forEach(examLink => {
        examLink.classList.remove('active');
      });
    }
  }

  /**
   * Bind DOM elements to this.elements object
   */
  bindElements() {
    // Stop any existing videos before rebinding elements
    if (this.elements && this.elements.videoWrapper) {
      this.stopAllVideosInWrapper();
    }

    this.elements = {
      // Video elements
      videoWrapper: document.querySelector(".formations-video-wrapper"),
      deferredMedia: null,
      overlay: document.querySelector(".formations-video-overlay"),

      // Control buttons
      playPauseBtn: document.querySelector('[data-action="play-pause"]'),
      muteBtn: document.querySelector('[data-action="mute"]'),
      fullscreenBtn: document.querySelector('[data-action="fullscreen"]'),

      // Progress elements
      progressBar: document.querySelector('[data-action="seek"]'),
      progressFill: document.querySelector(".formations-video-progress-fill"),
      progressHandle: document.querySelector(".formations-video-progress-handle"),
      currentTimeEl: document.querySelector(".formations-current-time"),
      durationEl: document.querySelector(".formations-duration"),

      // Other elements
      loadingOverlay: document.getElementById("formations-loading"),
      formationsSection: document.querySelector(".formations-lms")
    };

    // Update deferred media reference
    const videoWrapper = this.elements.videoWrapper;
    if (videoWrapper) {
      this.elements.deferredMedia = videoWrapper.querySelector("deferred-media");
    }

    this.logElementsFound();
  }

  /**
   * Log which elements were found for debugging
   */
  logElementsFound() {
    const found = {};
    Object.keys(this.elements).forEach(key => {
      found[key] = !!this.elements[key];
    });
    //console.log("Elements found:", found);
  }

  /**
   * Set up all event listeners
   */
  setupEventListeners() {
    // User interaction tracking
    document.addEventListener("click", () => { this.state.hasUserInteracted = true; }, { once: true });
    document.addEventListener("keydown", () => { this.state.hasUserInteracted = true; }, { once: true });

    // Browser navigation
    window.addEventListener("popstate", this.handlePopState.bind(this));
    window.addEventListener("beforeunload", this.hideLoadingOverlay.bind(this));

    // Fullscreen events
    this.setupFullscreenListeners();
  }

  /**
   * Set up fullscreen event listeners for cross-browser compatibility
   */
  setupFullscreenListeners() {
    const fullscreenEvents = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange"
    ];

    fullscreenEvents.forEach(event => {
      document.addEventListener(event, () => {
        const isFullscreen = this.isFullscreen();
        this.updateButtonState("fullscreen", isFullscreen);
      });
    });
  }

  /**
   * Initialize video controls
   */
  initializeVideoControls() {
    if (!this.elements.videoWrapper || !this.elements.overlay) return;

    // Initialize progress bar
    this.resetProgressBar();

    // Set up control button listeners
    this.setupControlButtons();

    // Set up progress bar interaction
    this.setupProgressBar();

    // Wait for video to load and initialize
    this.waitForVideoAndInitialize();
  }

  /**
   * Set up control button event listeners
   */
  setupControlButtons() {
    // Play/Pause button
    if (this.elements.playPauseBtn) {
      this.elements.playPauseBtn.addEventListener("click", this.handlePlayPause.bind(this));
    }

    // Mute button
    if (this.elements.muteBtn) {
      this.elements.muteBtn.addEventListener("click", this.handleMute.bind(this));
    }

    // Fullscreen button
    if (this.elements.fullscreenBtn) {
      this.elements.fullscreenBtn.addEventListener("click", this.handleFullscreen.bind(this));
    }
  }

  /**
   * Set up progress bar interaction
   */
  setupProgressBar() {
    if (!this.elements.progressBar) return;

    // Mouse events
    this.elements.progressBar.addEventListener("click", this.handleSeek.bind(this));
    this.elements.progressBar.addEventListener("mousedown", this.handleSeekStart.bind(this));

    // Touch events
    this.elements.progressBar.addEventListener("touchstart", this.handleSeekStart.bind(this));
    this.elements.progressBar.addEventListener("touchmove", this.handleSeekMove.bind(this));
    this.elements.progressBar.addEventListener("touchend", this.handleSeekEnd.bind(this));

    // Document-level events for dragging
    document.addEventListener("mousemove", this.handleSeekMove.bind(this));
    document.addEventListener("mouseup", this.handleSeekEnd.bind(this));
  }

  /**
   * Wait for video to load and initialize controls
   */
  waitForVideoAndInitialize() {
    const checkInterval = setInterval(() => {
      const video = this.getVideo();
      if (video && video.tagName === "VIDEO") {
        clearInterval(checkInterval);
        this.initializeVideoEventListeners(video);
        this.initializeButtonStates(video);
        this.preventMobileAutoplay(video);
      }
    }, this.config.videoCheckInterval);

    // Clear interval after timeout
    setTimeout(() => clearInterval(checkInterval), this.config.videoCheckTimeout);
  }

  /**
   * Initialize video event listeners
   */
  initializeVideoEventListeners(video) {
    const events = [
      { event: "play", handler: () => this.updateButtonState("play", true) },
      { event: "pause", handler: () => this.updateButtonState("play", false) },
      { event: "volumechange", handler: () => this.updateButtonState("mute", video.muted) },
      { event: "timeupdate", handler: () => this.updateProgress(video) },
      { event: "loadedmetadata", handler: () => this.updateProgress(video) },
      { event: "durationchange", handler: () => this.updateProgress(video) },
      { event: "canplay", handler: () => this.updateProgress(video) },
      { event: "loadeddata", handler: () => this.updateProgress(video) },
      { event: "loadstart", handler: () => this.updateProgress(video) }
    ];

    events.forEach(({ event, handler }) => {
      video.addEventListener(event, handler);
    });
  }

  /**
   * Initialize button states
   */
  initializeButtonStates(video) {
    this.updateButtonState("play", !video.paused);
    this.updateButtonState("mute", video.muted);

    if (video.readyState >= 1) {
      this.updateProgress(video);
    }
  }

  /**
   * Prevent autoplay on mobile devices
   */
  preventMobileAutoplay(video) {
    if (this.config.isMobile) {
      video.pause();
    }
  }

  /**
   * Handle play/pause button click
   */
  handlePlayPause(e) {
    e.preventDefault();
    e.stopPropagation();

    const video = this.getVideo();
    if (!video || video.tagName !== "VIDEO") return;

    if (video.paused) {
      video.play()
        .then(() => this.updateButtonState("play", true))
        .catch(console.error);
    } else {
      video.pause();
      this.updateButtonState("play", false);

      // Force update after delay to ensure pause state
      setTimeout(() => {
        if (video.paused) {
          this.updateButtonState("play", false);
        }
      }, 100);
    }
  }

  /**
   * Handle mute/unmute button click
   */
  handleMute(e) {
    e.preventDefault();
    e.stopPropagation();

    const video = this.getVideo();
    if (!video || video.tagName !== "VIDEO") return;

    video.muted = !video.muted;
    this.updateButtonState("mute", video.muted);
  }

  /**
   * Handle fullscreen button click
   */
  handleFullscreen(e) {
    e.preventDefault();
    e.stopPropagation();

    const video = this.getVideo();
    if (!video) return;

    if (this.isFullscreen()) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen(video);
    }
  }

  /**
   * Check if currently in fullscreen mode
   */
  isFullscreen() {
    const doc = document;
    return !!(
      doc.fullscreenElement ||
      doc["webkitFullscreenElement"] ||
      doc["mozFullScreenElement"]
    );
  }

  /**
   * Enter fullscreen mode
   */
  enterFullscreen(video) {
    const element = video.tagName === "VIDEO" ? video : this.elements.videoWrapper;

    if (element.requestFullscreen) {
      element.requestFullscreen().catch(console.error);
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitEnterFullscreen && video.tagName === "VIDEO") {
      video.webkitEnterFullscreen();
    }
  }

  /**
   * Exit fullscreen mode
   */
  exitFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    }
  }

  /**
   * Handle seek interaction start
   */
  handleSeekStart(e) {
    e.preventDefault();
    e.stopPropagation();
    this.state.isDragging = true;
    this.handleSeek(e);
  }

  /**
   * Handle seek interaction move
   */
  handleSeekMove(e) {
    if (!this.state.isDragging) return;
    e.preventDefault();
    this.handleSeek(e);
  }

  /**
   * Handle seek interaction end
   */
  handleSeekEnd(e) {
    if (e) e.preventDefault();
    this.state.isDragging = false;
  }

  /**
   * Handle seek to specific time
   */
  handleSeek(e) {
    const video = this.getVideo();
    if (!video || video.tagName !== "VIDEO" || !video.duration) return;

    const rect = this.elements.progressBar.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = percentage * video.duration;

    video.currentTime = newTime;
    this.updateProgress(video);
  }

  /**
   * Update button states
   */
  updateButtonState(type, state) {
    const buttonConfigs = {
      play: {
        button: this.elements.playPauseBtn,
        activeIcon: ".pause-icon",
        inactiveIcon: ".play-icon"
      },
      mute: {
        button: this.elements.muteBtn,
        activeIcon: ".mute-icon",
        inactiveIcon: ".unmute-icon"
      },
      fullscreen: {
        button: this.elements.fullscreenBtn,
        activeIcon: ".fullscreen-exit-icon",
        inactiveIcon: ".fullscreen-enter-icon"
      }
    };

    const config = buttonConfigs[type];
    if (!config || !config.button) return;

    const activeIcon = config.button.querySelector(config.activeIcon);
    const inactiveIcon = config.button.querySelector(config.inactiveIcon);

    if (activeIcon && inactiveIcon) {
      if (state) {
        activeIcon.style.display = "block";
        inactiveIcon.style.display = "none";
      } else {
        activeIcon.style.display = "none";
        inactiveIcon.style.display = "block";
      }
    }
  }

  /**
   * Update progress bar and time displays
   */
  updateProgress(video) {
    if (!video || !this.elements.progressFill || !this.elements.progressHandle) return;
    if (!video.duration || isNaN(video.duration) || video.duration === 0) return;

    const currentTime = video.currentTime || 0;
    const duration = video.duration;
    const percentage = Math.max(0, Math.min(100, (currentTime / duration) * 100));

    // Update progress bar
    this.elements.progressFill.style.width = `${percentage}%`;
    this.elements.progressHandle.style.left = `${percentage}%`;

    // Update time displays
    if (this.elements.currentTimeEl) {
      this.elements.currentTimeEl.textContent = this.formatTime(currentTime);
    }
    if (this.elements.durationEl) {
      this.elements.durationEl.textContent = this.formatTime(duration);
    }
  }

  /**
   * Format time in MM:SS format
   */
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  /**
   * Reset progress bar to initial state
   */
  resetProgressBar() {
    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = "0%";
    }
    if (this.elements.progressHandle) {
      this.elements.progressHandle.style.left = "0%";
    }
  }

  /**
   * Get video element, with option to force load deferred media
   */
  getVideo(forceLoad = false) {
    if (!this.elements.videoWrapper) return null;

    // Try to get existing video
    let video = this.elements.videoWrapper.querySelector("video");
    if (video) return video;

    // Try to load deferred media if not loaded
    if (this.elements.deferredMedia && !this.elements.deferredMedia.getAttribute("loaded")) {
      const posterButton = this.elements.deferredMedia.querySelector("button");
      if (posterButton && (!this.config.isMobile || forceLoad)) {
        posterButton.click();
      }
    }

    // Return video or iframe
    return this.elements.videoWrapper.querySelector("video") ||
           this.elements.videoWrapper.querySelector("iframe");
  }

  /**
   * Save current video state before navigation
   */
  saveVideoState() {
    const video = this.getVideo();
    if (video && video.tagName === "VIDEO") {
      this.state.currentVideo = {
        isMuted: video.muted || false,
        volume: video.volume || 1.0,
        currentTime: video.currentTime || 0,
        isPlaying: !video.paused,
        playbackRate: video.playbackRate || 1.0
      };

      // Stop current video completely to prevent audio bleeding
      video.pause();
      video.currentTime = 0;
      video.muted = true; // Mute to ensure no audio continues playing
      video.volume = 0; // Set volume to 0 as additional safety

      console.log("Video state saved:", this.state.currentVideo);
    }
  }

  /**
   * Apply saved video state to new video
   */
  applyVideoState(video, autoPlay = true) {
    if (!video || video.tagName !== "VIDEO") return;

    try {
      // First, ensure any other videos in the wrapper are completely stopped
      this.stopAllVideosInWrapper();

      // Apply saved state
      video.volume = this.state.currentVideo.volume;
      video.muted = this.state.currentVideo.isMuted;
      video.playbackRate = this.state.currentVideo.playbackRate;

      // Update UI
      this.updateButtonState("mute", this.state.currentVideo.isMuted);

      // Handle autoplay
      if (autoPlay) {
        this.handleAutoPlay(video);
      }

      console.log("Video state applied:", this.state.currentVideo);
    } catch (error) {
      console.error("Error applying video state:", error);
    }
  }

  /**
   * Stop all videos in the video wrapper to prevent audio bleeding
   */
  stopAllVideosInWrapper() {
    if (!this.elements.videoWrapper) return;

    // Stop all video elements
    const videos = this.elements.videoWrapper.querySelectorAll("video");
    videos.forEach(video => {
      video.pause();
      video.currentTime = 0;
      video.muted = true;
      video.volume = 0;
    });

    // Stop all iframe elements (for external videos)
    const iframes = this.elements.videoWrapper.querySelectorAll("iframe");
    iframes.forEach(iframe => {
      // For iframes, we can't directly control them, but we can try to stop them
      // by setting src to empty or reloading
      if (iframe.src) {
        iframe.src = iframe.src;
      }
    });
  }

  /**
   * Handle autoplay with proper mobile considerations
   */
  handleAutoPlay(video = null) {
    if (!video) {
      // Handle initial autoplay
      if (this.elements.deferredMedia &&
          this.elements.deferredMedia.hasAttribute("autoplay") &&
          !this.config.isMobile) {
        const posterButton = this.elements.deferredMedia.querySelector("button");
        if (posterButton) {
          posterButton.click();
        }
      }
      return;
    }

    // Handle autoplay for specific video
    const shouldMuteForAutoplay = this.config.isMobile ||
                                 (!this.state.currentVideo.isMuted && !this.state.hasUserInteracted);

    if (shouldMuteForAutoplay) {
      video.muted = true;
    }

    const attemptPlay = () => {
      video.play()
        .then(() => {
          // Restore mute state after successful autoplay
          if (shouldMuteForAutoplay) {
            setTimeout(() => {
              video.muted = this.state.currentVideo.isMuted;
              this.updateButtonState("mute", this.state.currentVideo.isMuted);
            }, this.config.isMobile ? 500 : 1000);
          }
          this.updateButtonState("play", true);
          console.log("Video auto-played with preserved state");
        })
        .catch(err => {
          console.log("Auto-play failed:", err);
          this.updateButtonState("play", false);
        });
    };

    // Wait for video to be ready
    if (video.readyState >= 2) {
      attemptPlay();
    } else {
      video.addEventListener("loadeddata", attemptPlay, { once: true });
      setTimeout(attemptPlay, 1000); // Fallback
    }
  }

  /**
   * Initialize navigation interceptors
   */
  initializeNavigationInterceptors() {
    // Navigation links
    const navLinks = document.querySelectorAll('[data-loading="true"]');
    navLinks.forEach(link => {
      link.removeEventListener("click", this.handleNavigationClick.bind(this));
      link.addEventListener("click", this.handleNavigationClick.bind(this));
    });

    // Lesson items
    const lessonItems = document.querySelectorAll(".formations-lesson-item");
    lessonItems.forEach(item => {
      item.removeEventListener("click", this.handleNavigationClick.bind(this));
      item.addEventListener("click", this.handleNavigationClick.bind(this));
    });

    // Exam links - special handling
    const examLinks = document.querySelectorAll('[data-exam-link="true"]');
    //console.log('🔍 Found exam links:', examLinks.length, examLinks);
    examLinks.forEach(link => {
      //console.log('📎 Attaching exam click handler to:', link);
      link.removeEventListener("click", this.handleExamClick.bind(this));
      link.addEventListener("click", this.handleExamClick.bind(this));
    });
  }

  /**
   * Handle navigation click
   */
  handleNavigationClick(event) {
    const element = event.currentTarget;
    const href = element.getAttribute("href");
    
    //console.log('🔍 NAVIGATION DEBUG - Clicked element href:', href);
    //console.log('🔍 NAVIGATION DEBUG - Current URL before navigation:', window.location.href);
    
    // Remove exam-loaded class when navigating to lessons
    //console.log('📚 Navigating to lesson - removing exam-loaded class from body');
    document.body.classList.remove('exam-loaded');
    
    // Extract formation data from data attributes
    const formationData = {
      formationId: element.dataset.formationId,
      formationTitle: element.dataset.formationTitle,
      lessonNumber: element.dataset.lessonNumber
    };
    
    if (href && href !== "#") {
      this.navigateToLesson(href, event, formationData);
    }
  }

  /**
   * Handle exam link click
   */
  handleExamClick(event) {
    //console.log('🚀 handleExamClick triggered!', event.currentTarget);
    event.preventDefault();
    
    //console.log('📝 Exam link clicked - adding exam-loaded class to body');
    
    // Add exam-loaded class to body
    document.body.classList.add('exam-loaded');
    //console.log('✅ Added exam-loaded class to body');
    
    // Add active state to exam link - simple and direct
    // Remove active from all lesson items (including both exam links)
    document.querySelectorAll('.formations-lesson-item').forEach(item => {
      item.classList.remove('active');
    });
    
    // Add active to ALL exam links (mobile and desktop)
    document.querySelectorAll('.formations-exam-link').forEach(examLink => {
      examLink.classList.add('active');
    });
    //console.log('✅ Added active class to all exam links');
    
    // Update URL to remove lesson parameter and add exam hash
    this.updateUrlForExam();
    
    // Extract formation data for potential tracking
    const element = event.currentTarget;
    const formationData = {
      formationId: element.dataset.formationId,
      formationTitle: element.dataset.formationTitle
    };
    
    //console.log('📊 Exam mode activated for formation:', formationData);
  }

  /**
   * Update URL for exam mode - remove lesson parameter, keep banner if present
   */
  updateUrlForExam() {
    const currentUrl = new URL(window.location);
    const params = new URLSearchParams(currentUrl.search);
    
    //console.log('🔍 EXAM URL DEBUG - Current URL:', currentUrl.href);
    //console.log('🔍 EXAM URL DEBUG - Current params:', params.toString());
    //console.log('🔍 EXAM URL DEBUG - Banner param before:', params.get('banner'));
    
    // Remove lesson parameter and add exam parameter, keeping all other parameters (including banner)
    params.delete('lesson');
    params.set('exam', '1');
    
    //console.log('🔍 EXAM URL DEBUG - Banner param after lesson delete:', params.get('banner'));
    //console.log('🔍 EXAM URL DEBUG - All params after lesson delete:', params.toString());
    
    // Build new URL
    let newUrl = currentUrl.pathname;
    if (params.toString()) {
      newUrl += '?' + params.toString();
    }
    
    // Update browser history
    const stateData = {
      exam: true
    };
    
    // Keep banner parameter in state if it exists
    if (params.has('banner')) {
      stateData.banner = params.get('banner');
      //console.log('🔍 EXAM URL DEBUG - Banner preserved in state:', params.get('banner'));
    } else {
      //console.log('🔍 EXAM URL DEBUG - No banner parameter found!');
    }
    
    //console.log('🔗 EXAM URL DEBUG - Final URL:', newUrl);
    //console.log('🔗 EXAM URL DEBUG - State data:', stateData);
    window.history.pushState(stateData, '', newUrl);
  }

  /**
   * Handle browser popstate event
   */
  handlePopState(event) {
    // Check URL for exam parameter instead of state
    const urlParams = new URLSearchParams(window.location.search);
    const isExamMode = urlParams.get('exam') === '1';
    
    if (isExamMode) {
      // Handle exam state
      //console.log('📝 Popstate: Navigating to exam mode');
      document.body.classList.add('exam-loaded');
      document.body.classList.remove('lesson-loaded');
        
      // Set exam link as active
      document.querySelectorAll('.formations-lesson-item').forEach(item => {
        item.classList.remove('active');
      });
      // Add active to ALL exam links (mobile and desktop)
      document.querySelectorAll('.formations-exam-link').forEach(examLink => {
        examLink.classList.add('active');
      });
        
      return; // Don't navigate to lesson
    }
    
    // Handle lesson navigation for non-exam URLs
    if (event.state && (event.state.lesson || event.state.banner)) {
      let url = window.location.pathname;
      const params = new URLSearchParams();

      if (event.state.lesson) params.set("lesson", event.state.lesson);
      if (event.state.banner) params.set("banner", event.state.banner);

      if (params.toString()) {
        url += "?" + params.toString();
      }

      this.navigateToLesson(url, null, null);
    } else {
      window.location.reload();
    }
  }

  /**
   * Navigate to lesson via AJAX
   */
  async navigateToLesson(url, event = null, formationData = null) {
    if (event) event.preventDefault();

    // Remove exam-loaded class when navigating to any lesson
    //console.log('📚 Navigating to lesson via AJAX - removing exam-loaded class from body');
    document.body.classList.remove('exam-loaded');

    try {
      this.showLoadingOverlay();
      this.saveVideoState();

      const { lesson, banner } = this.parseUrl(url);
      
      // Update progress if we have formation data and global update function is available
      if (formationData && formationData.formationId && window.LVFormationsProgress && window.LVFormationsProgress.update) {
        //console.log("Updating formation progress before navigation...");
        try {
          const progressResult = await window.LVFormationsProgress.update(
            formationData.formationId,
            formationData.formationTitle,
            formationData.lessonNumber || lesson
          );
          
          if (progressResult.success) {
            //console.log("✅ Formation progress updated successfully");
          } else {
            //console.warn("⚠️ Formation progress update failed:", progressResult.error);
            // Continue with navigation even if progress update fails
          }
        } catch (progressError) {
          console.error("❌ Error updating formation progress:", progressError);
          // Continue with navigation even if progress update fails
        }
      }
      
      const sectionUrl = this.buildSectionUrl(lesson, banner);

      console.log("Fetching section:", sectionUrl);

      const response = await fetch(sectionUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlText = await response.text();
      const newSection = this.parseNewSection(htmlText);

      if (!newSection) {
        throw new Error("New section content not found");
      }

      // Update page
      history.pushState({ lesson, banner }, "", url);
      this.replaceSection(newSection);
      this.reinitializeAfterNavigation();

      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });

      console.log("Navigation completed successfully");
    } catch (error) {
      console.error("AJAX navigation failed:", error);
      window.location.href = url; // Fallback
    } finally {
      setTimeout(() => this.hideLoadingOverlay(), this.config.loadingMinTime);
    }
  }

  /**
   * Parse URL to extract lesson and banner parameters
   */
  parseUrl(url) {
    const urlObj = new URL(url, window.location.origin);
    return {
      lesson: urlObj.searchParams.get("lesson"),
      banner: urlObj.searchParams.get("banner")
    };
  }

  /**
   * Build section URL for AJAX request
   */
  buildSectionUrl(lesson, banner) {
    let sectionUrl = `${window.location.pathname}?section_id=${this.config.sectionId}`;
    if (lesson) sectionUrl += `&lesson=${lesson}`;
    if (banner) sectionUrl += `&banner=${banner}`;
    return sectionUrl;
  }

  /**
   * Parse new section from HTML string
   */
  parseNewSection(htmlText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, "text/html");
    return doc.querySelector(".formations-lms");
  }

  /**
   * Replace current section with new content
   */
  replaceSection(newSection) {
    const currentSection = document.querySelector(".formations-lms");
    if (currentSection && currentSection.parentNode) {
      // Stop all videos in the current section before replacing
      const videos = currentSection.querySelectorAll("video");
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
        video.muted = true;
        video.volume = 0;
      });
      
      currentSection.parentNode.replaceChild(newSection, currentSection);
    }
  }

  /**
   * Reinitialize after navigation
   */
  reinitializeAfterNavigation() {
    this.bindElements();
    this.initializeVideoControls();
    this.initializeNavigationInterceptors();
    this.setupMobileControls();

    // Apply video state
    if (this.config.isMobile) {
      this.waitForVideoAndApplyState();
    } else {
      setTimeout(() => {
        const video = this.getVideo(true);
        if (video) {
          this.applyVideoState(video, true);
        }
      }, 500);
    }
  }

  /**
   * Wait for mobile video to load and apply state
   */
  waitForVideoAndApplyState() {
    let attempts = 0;
    const maxAttempts = this.config.maxNavigationAttempts;

    const tryApplyState = () => {
      attempts++;
      console.log(`Attempt ${attempts} to get mobile video`);

      let video = this.getVideo(false);
      if (!video) {
        video = this.getVideo(true);
      }

      if (video && video.tagName === "VIDEO") {
        console.log("Mobile video found, applying state");
        setTimeout(() => {
          this.applyVideoState(video, true);
        }, 200);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(tryApplyState, this.config.navigationRetryDelay);
      } else {
        console.log("Mobile video loading timeout");
      }
    };

    tryApplyState();
  }

  /**
   * Set up mobile touch controls
   */
  setupMobileControls() {
    if (!this.config.isMobile || !this.elements.videoWrapper || !this.elements.overlay) return;

    // Show controls on tap
    this.elements.videoWrapper.addEventListener("click", (e) => {
      if (!e.target.closest(".formations-control-btn") && !this.state.isControlAction) {
        this.showMobileControls();
      }
      this.state.isControlAction = false;
    });

    // Handle control button interactions
    this.elements.overlay.addEventListener("click", (e) => {
      const controlBtn = e.target.closest(".formations-control-btn");
      if (controlBtn) {
        this.state.isControlAction = true;
        this.handleMobileControlInteraction(controlBtn);
      }
    });

    // Prevent touch interference
    this.elements.overlay.addEventListener("touchstart", (e) => {
      if (e.target.closest(".formations-control-btn")) {
        e.stopPropagation();
      }
    });

    // Handle mobile deferred media
    this.handleMobileDeferredMedia();
  }

  /**
   * Show mobile controls
   */
  showMobileControls() {
    this.elements.overlay.classList.add("show-controls");
    clearTimeout(this.state.controlsTimeout);
    this.state.controlsTimeout = setTimeout(() => {
      this.elements.overlay.classList.remove("show-controls");
    }, this.config.controlsHideDelay);
  }

  /**
   * Handle mobile control interaction
   */
  handleMobileControlInteraction(controlBtn) {
    clearTimeout(this.state.controlsTimeout);

    const delay = controlBtn.dataset.action === "play-pause" ?
                 (this.getVideo()?.paused ? 6000 : 5000) :
                 this.config.controlsHideDelay;

    this.state.controlsTimeout = setTimeout(() => {
      this.elements.overlay.classList.remove("show-controls");
    }, delay);
  }

  /**
   * Handle mobile deferred media loading
   */
  handleMobileDeferredMedia() {
    if (!this.elements.playPauseBtn) return;

    let deferredMediaHandled = false;

    this.elements.playPauseBtn.addEventListener("click", (e) => {
      if (this.elements.deferredMedia &&
          !this.elements.deferredMedia.getAttribute("loaded") &&
          !deferredMediaHandled) {

        deferredMediaHandled = true;
        e.preventDefault();
        e.stopPropagation();

        const posterButton = this.elements.deferredMedia.querySelector("button");
        if (posterButton) {
          posterButton.click();
          setTimeout(() => {
            const video = this.getVideo();
            if (video && video.tagName === "VIDEO") {
              video.play()
                .then(() => this.updateButtonState("play", true))
                .catch(console.error);
            }
          }, 500);
        }
      }
    });
  }

  /**
   * Show loading overlay
   */
  showLoadingOverlay() {
    if (this.elements.loadingOverlay) {
      console.log("Showing loading overlay");
      this.elements.loadingOverlay.classList.add("active");
    }
  }

  /**
   * Hide loading overlay
   */
  hideLoadingOverlay() {
    if (this.elements.loadingOverlay) {
      console.log("Hiding loading overlay");
      this.elements.loadingOverlay.classList.remove("active");
    }
  }

  /**
   * Ensure video wrapper has content
   */
  ensureVideoWrapper() {
    if (!this.elements.videoWrapper) return;

    if (!this.elements.videoWrapper.hasChildNodes() || this.elements.videoWrapper.children.length === 0) {
      const placeholder = document.createElement("div");
      placeholder.style.cssText = "width: 100%; height: 100%; background: #000000; position: absolute; top: 0; left: 0;";
      this.elements.videoWrapper.appendChild(placeholder);
    }
  }

  /**
   * Set up video wrapper observer
   */
  setupVideoObserver() {
    if (!this.elements.videoWrapper) return;

    const observer = new MutationObserver(() => this.ensureVideoWrapper());
    observer.observe(this.elements.videoWrapper, { childList: true, subtree: true });
  }

  /**
   * Detect if device is mobile
   */
  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }

  /**
   * Get section ID for AJAX requests
   */
  getSectionId() {
    const formationsSection = document.querySelector(".formations-lms")?.closest(".shopify-section");
    return formationsSection ?
           formationsSection.id.replace("shopify-section-", "") :
           "main-formations-v2";
  }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new FormationsVideoPlayer();
});
